import { FC, useMemo } from "react";
import { format } from "date-fns";
import { Badge, Box, Tooltip, useTheme } from "@mui/material";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers-pro";
import findInAbsenceRequestDays from "src/utils/Company/Absences/findInAbsenceRequestDays";
import isDayExcluded from "src/utils/Company/Absences/isDayExcluded";
import { IAbsenceRequestDay } from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import { IExcludedDay } from "src/Pages/Company/Absences/Calendars/Calendars.models";
import {
  getAbsenceRequestDayStyles,
  getPickerStyles,
  getRangeIntervalStyles,
} from "./Datepicker.styles";

interface ICustomPickersDayProps extends PickersDayProps {
  absenceRequestDays: IAbsenceRequestDay[];
  excludedDays: IExcludedDay[];
  plannedDays: string[];
  multipleRequestsPerDayAllowed: boolean;
  billInCalendarDays: boolean;
}

const CustomPickersDay: FC<ICustomPickersDayProps> = ({
  absenceRequestDays = [],
  excludedDays = [],
  plannedDays = [],
  multipleRequestsPerDayAllowed,
  billInCalendarDays,
  ...props
}) => {
  const theme = useTheme();

  const { day, outsideCurrentMonth } = props;
  const parsedToISO = format(new Date(day), "yyyy-MM-dd");
  const requestDay = findInAbsenceRequestDays(day, absenceRequestDays);
  const isRequestDay = !!requestDay && requestDay.absenceName;
  // Normal excluded days (i.e. weekends or holidays)
  const isExcluded = isDayExcluded(day, excludedDays);
  // Excluded days on absence types counting excluded days to balance
  const isCustomExcluded = billInCalendarDays && isExcluded;
  const plannedDaysSet = useMemo(() => new Set(plannedDays), [plannedDays]);
  const isPlannedDay = plannedDaysSet.has(parsedToISO);

  if (isRequestDay) {
    const dayElement = (
      <PickersDay
        {...props}
        disabled={
          !multipleRequestsPerDayAllowed ||
          !requestDay.multipleRequestsPerDayAllowed
        }
        sx={getPickerStyles(!!isRequestDay, requestDay?.bgColor)}
        data-testid="absence-datepicker-request-day"
      >
        <Tooltip title={requestDay.absenceName} placement="top" arrow>
          <Box
            component="span"
            sx={getAbsenceRequestDayStyles(requestDay?.color)}
          >
            {format(day, "d")}
          </Box>
        </Tooltip>
      </PickersDay>
    );

    return isPlannedDay && !outsideCurrentMonth ? (
      <Badge badgeContent="" variant="dot" color="primary" overlap="circular">
        {dayElement}
      </Badge>
    ) : (
      dayElement
    );
  }

  const dayElement = (
    <PickersDay
      {...props}
      sx={getRangeIntervalStyles(theme, isExcluded, isCustomExcluded)}
      data-testid="absence-datepicker-day"
    />
  );

  return isPlannedDay && !outsideCurrentMonth ? (
    <Badge badgeContent="" variant="dot" color="primary" overlap="circular">
      {dayElement}
    </Badge>
  ) : (
    dayElement
  );
};

export default CustomPickersDay;
